import { useState } from "react";
import { z } from "zod";
import { subscriptionService, taskService } from "@miplatform/api";
import {
  ENTITY_NAMES,
  HTTP_STATUSES,
  INPUT_TYPES,
  RECORD_TYPES,
} from "@miplatform/constants";
import { useAssignmentTemplate, useComponentState } from "@miplatform/hooks";
import { createUUID } from "@miplatform/utils";

const initialData = {
  name: "",
  defaultCausedByTradeId: null,
  defaultResolvedByTradeId: null,
  canBeSelfResolved: false,
  recordType: "",
  assignmentTemplateIds: [],
  canBeNotStarted: false,
};

const prepareIssueTemplateFields = ({
  assignmentTemplates,
  trades,
  isModal = false,
}) => {
  return [
    {
      label: "Name",
      name: "name",
      width: isModal ? "100%" : "50%",
      validationRule: z
        .string()
        .min(1, `${ENTITY_NAMES.ISSUE_TEMPLATE} name is required.`),
      required: true,
    },
    {
      label: "Record Type",
      name: "recordType",
      width: isModal ? "100%" : "50%",
      fieldType: INPUT_TYPES.SELECT,
      options: Object.values(RECORD_TYPES).map((item) => ({
        label: item,
        value: item,
      })),
      validationRule: z.string().min(1, "Record type is required."),
      required: true,
    },
    {
      label: "Caused By Trade",
      name: "defaultCausedByTradeId",
      width: isModal ? "100%" : "50%",
      fieldType: INPUT_TYPES.SELECT,
      options: trades?.map(({ name, id }) => ({
        label: name,
        value: id,
      })),
      required: true,
      validationRule: z.string().min(1, "Caused By Trade is required."),
    },
    {
      label: "Resolved By Trade",
      name: "defaultResolvedByTradeId",
      width: isModal ? "100%" : "50%",
      fieldType: INPUT_TYPES.SELECT,
      options: trades?.map(({ name, id }) => ({
        label: name,
        value: id,
      })),
      required: true,
      validationRule: z.string().min(1, "Resolved By Trade is required."),
    },
    {
      label: "Assignment Types",
      name: "assignmentTemplateIds",
      width: "50%",
      fieldType: INPUT_TYPES.MULTI_SELECT,
      options: assignmentTemplates,
      validationRule: z.array(z.string()).min(1, "Assignment Type required."),
      required: true,
      style: {
        flexGrow: 0,
      },
    },
    {
      label: "Can be resolved on same attempt",
      name: "canBeSelfResolved",
      width: "100%",
      fieldType: INPUT_TYPES.SWITCH,
      validationRule: z.boolean(),
    },
    {
      label: "Can be not started",
      name: "canBeNotStarted",
      width: "100%",
      fieldType: INPUT_TYPES.SWITCH,
      validationRule: z.boolean(),
    },
  ];
};

const getIssueTemplateUUID = createUUID();
const getUpdateIssueTemplateUUID = createUUID();
const getTradesUUID = createUUID();

export const useIssueTemplateDetailsViewModel = () => {
  const stateManager = useComponentState();
  const [issueTemplate, setIssueTemplate] = useState([]);
  const [trades, setTrades] = useState([]);
  const { assignmentTemplates, getAssignmentTemplates } = useAssignmentTemplate(
    {
      service: taskService,
    }
  );

  const getIssueTemplate = (id) => {
    stateManager.abort(getIssueTemplateUUID);
    stateManager.run(async () => {
      const response = await taskService.issueTemplates.getById(id);
      if (response.status === HTTP_STATUSES.OK) {
        setIssueTemplate({
          ...response.data,
          defaultCausedByTradeId: response.data.defaultCausedByTradeId ?? null,
          defaultResolvedByTradeId:
            response.data.defaultResolvedByTradeId ?? null,
        });
      }
    }, getIssueTemplateUUID);
  };

  const getTrades = () => {
    stateManager.abort(getTradesUUID);
    stateManager.run(async () => {
      const response = await subscriptionService.trades.getAll();
      if (response.status === HTTP_STATUSES.OK) {
        setTrades(response.data);
      }
    }, getTradesUUID);
  };

  const updateIssueTemplate = (id, data) => {
    stateManager.abort(getUpdateIssueTemplateUUID);
    return stateManager.run(async () => {
      const response = await taskService.issueTemplates.update({
        id,
        data,
        toastMessageConfig: {
          success: {
            text: ` ${issueTemplate.name} was updated successfully.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setIssueTemplate(response.data);
      }
    }, getUpdateIssueTemplateUUID);
  };

  return {
    initialData,
    prepareIssueTemplateFields,
    stateManager,
    issueTemplate,
    getIssueTemplate,
    updateIssueTemplate,
    assignmentTemplates,
    getAssignmentTemplates,
    getTrades,
    trades,
  };
};
